import { ChartBarIcon } from '@heroicons/react/24/outline';
import TimeSeriesChart from '../TimeSeriesChart';
import HoldTimeHistogram from '../HoldTimeHistogram';
import { readPoolStats } from './readPoolStats';

function formatPeakTime(tMs) {
  const date = new Date(tMs);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

export default function PoolStatsPanel({ analysis }) {
  const stats = readPoolStats(analysis);

  if (!stats) {
    return (
      <div className="card text-center py-8 text-gray-500">
        <ChartBarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p className="font-medium">No chart data available</p>
        <p className="text-sm mt-1">Run a new analysis to generate visualizations</p>
      </div>
    );
  }

  const xDomain =
    Number.isFinite(stats.domain.startMs) && Number.isFinite(stats.domain.endMs)
      ? [stats.domain.startMs, stats.domain.endMs]
      : undefined;
  const hasUtilization = stats.utilization.some((point) => point.actual != null);
  const hasConnections = stats.connections.some((point) => point.active != null || point.idle != null);
  const hasThroughput = stats.throughput.some((point) => point.acquires || point.releases);
  const hasHistogram = stats.histogram && Object.keys(stats.histogram).length > 0;

  if (!hasUtilization && !hasConnections && !hasThroughput && !hasHistogram) {
    return (
      <div className="card text-center py-8 text-gray-500">
        <ChartBarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p className="font-medium">No chart data available</p>
        <p className="text-sm mt-1">Run a new analysis to generate visualizations</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {stats.notices.map((notice) => (
        <div key={notice} className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {notice}
        </div>
      ))}

      {hasUtilization && (
        <TimeSeriesChart
          title="Pool Utilization"
          data={stats.utilization}
          lines={[{ key: 'actual', name: 'Utilization', color: '#7c3aed' }]}
          type="line"
          yDomain={[0, 100]}
          xDomain={xDomain}
          band={{ min: 'min', span: 'span', color: '#8b5cf6', name: 'Min–max' }}
          referenceY={stats.peak.value}
          height={280}
        />
      )}

      {stats.peaks.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Peak observations</h3>
          {stats.peak.at === 'unlocated' && stats.peak.value != null && (
            <p className="text-xs text-amber-800 mb-3">
              Card peak {stats.peak.value}% is not in this series. The horizontal reference uses the card value; no X timestamp was invented.
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="py-2 pr-3 font-medium">Time</th>
                  <th className="py-2 pr-3 font-medium text-right">Util</th>
                  <th className="py-2 font-medium text-right">Active</th>
                </tr>
              </thead>
              <tbody>
                {stats.peaks.map((peak) => (
                  <tr key={`${peak.tMs}-${peak.connId || ''}`} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-gray-800">{formatPeakTime(peak.tMs)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{Number(peak.utilization).toFixed(2)}%</td>
                    <td className="py-2 text-right tabular-nums">{peak.active}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {hasConnections && (
        <TimeSeriesChart
          title="Active vs Idle Connections"
          data={stats.connections}
          lines={[
            { key: 'active', name: 'Active', color: '#ef4444' },
            { key: 'idle', name: 'Idle', color: '#22c55e' },
          ]}
          type="area"
          yDomain={Number.isFinite(stats.poolMax) ? [0, stats.poolMax] : undefined}
          xDomain={xDomain}
          height={250}
        />
      )}

      {(hasThroughput || hasHistogram) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {hasThroughput && (
            <TimeSeriesChart
              title="Connection Throughput"
              data={stats.throughput}
              lines={[
                { key: 'acquires', name: 'Acquires', color: '#3b82f6' },
                { key: 'releases', name: 'Releases', color: '#22c55e' },
              ]}
              type="line"
              height={250}
            />
          )}
          {hasHistogram && (
            <HoldTimeHistogram
              title="Hold Time Distribution"
              histogram={stats.histogram}
              height={250}
            />
          )}
        </div>
      )}
    </div>
  );
}
